import React, { useState, useEffect } from 'react';
import { Tab, Row, Col, NavItem, Nav } from 'react-bootstrap';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import ChangePassword from 'ChangePassword';
import { ViewApplicant } from 'Applicants/ViewApplicant';
import InterviewForm from 'InterviewForm';
import NoAccess from './NoAccess';
import styles from './applicant.module.scss';
import { withRouter } from 'react-router';
import { getApplicantStatus } from '_utils/utils';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Modal from '_components/utils/Modal/Modal';
import DatePicker from 'react-datepicker';
import { applicantsService } from '_services/applicants.service';
import moment from 'moment';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { dateTimeConstants } from '_constants';
import { CustomDatePickerInput } from '_components/CustomDateInput';

const TAB_VALUES = [
  {
    title: 'Job Application',
    key: 'applicant',
    permission: 'Manage Applicants',
  },
  {
    title: 'Interview Forms',
    key: 'interview',
    permission: 'Manage Interview Forms',
  },
];

const ApplicantsTab = ({
  hash,
  applnType,
  goBack,
  email,
  sendMail,
  editApplicant,
  viewHistory,
  applicantView,
  isApplicationEditable,
  ...props
}) => {
  const [activeTab, setActiveTab] = useState('no_reports_found');
  const permissions =
    useSelector((state) => state.permission?.permissions?.data) || [];
  const applicants = useSelector((state) => state.applicants?.list?.data) || {};
  const [enableStatusModal, setEnableStatusModal] = useState(false);
  const [timeRestrictionOpen, setTimeRestrictionOpen] = useState(false);
  const [startDate, setStartDate] = useState(
    applicants?.login_time_restriction
      ? moment(applicants.login_time_restriction, 'YYYY-MM-DD').toDate()
      : new Date(),
  );

  let selfView = props.location.pathname.includes('settings'); //condition returns true of the user is viewing their own Job application

  const TABS = React.useMemo(() => {
    if (selfView) {
      return TAB_VALUES;
    } else {
      return (
        TAB_VALUES?.filter(({ permission }) =>
          permissions.includes(permission),
        ) ?? []
      );
    }
  }, [permissions]);

  const activeLabel =
    TABS?.filter(({ key }) => key === activeTab)[0]?.title || '';

  useEffect(() => {
    const actTab = TABS.length ? TABS[0].key : 'no_reports_found';
    setActiveTab(actTab);
  }, [permissions]);

  const allowedStatuses = ['Received', 'In Review', 'Offered Position'];
  const includedStatusKeys = Object.entries(getApplicantStatus)
    .filter(([_, label]) => allowedStatuses.includes(label))
    .map(([key]) => Number(key));

  const mutation = useMutation({
    mutationFn: (formData) => applicantsService.updateTimeRestriction(formData),
    onSuccess: (res) => {
      if (res?.success) {
        toast.success('Updated Time Restriction successfully.');
        setTimeRestrictionOpen(false);
      } else {
        toast.error('Failed To Update Time Restriction.');
      }
    },
    onError: () => {
      toast.error('Failed To Update Time Restriction.');
    },
  });

  const handleSave = () => {
    const data = new FormData();
    data.append('hash', hash);
    data.append(
      'login_time_restriction',
      moment(startDate).format('YYYY-MM-DD'),
    );

    mutation.mutate(data);
  };

  useEffect(() => {
    if (applicants?.login_time_restriction) {
      const newDate = moment(
        applicants.login_time_restriction,
        'YYYY-MM-DD',
      ).toDate();
      if (!startDate || newDate.getTime() !== startDate.getTime()) {
        setStartDate(newDate);
      }
    }
  }, [applicants]);

  return (
    <div className={styles.applicant__container}>
      {applicantView && (
        <>
          <div className='applicants_container application_details'>
            <div className='breadcrumbs'>
              <span className='green'>
                <Link to='/applicants' className='green'>
                  Applicants
                </Link>
              </span>{' '}
              / {activeLabel}
            </div>
          </div>
          <div className={styles.applicant_tab_header}>
            <h1 className={styles.page_title}>
              {applicants.first_name
                ? `${applicants.first_name} ${applicants.middle_initial} ${applicants.last_name}`
                : ''}
            </h1>
            <div className={styles.header_btn_grp}>
              {permissions.includes('Manage Passwords') && (
                <ChangePassword
                  userType='applicant'
                  user={applicants}
                  isAdmin={true}
                />
              )}
              {permissions.includes('Email Applicants') && (
                <button className='button' onClick={() => sendMail(email)}>
                  Email
                </button>
              )}
              {permissions.includes('Manage Applicants') &&
                includedStatusKeys?.includes(applicants.status) && (
                  <button
                    className='button'
                    onClick={() => setEnableStatusModal(true)}
                  >
                    Update Status
                  </button>
                )}

              {permissions.includes('Manage Applicants') &&
                applicants.status == 2 && (
                  <button
                    className='button'
                    onClick={() => setTimeRestrictionOpen(true)}
                  >
                    Update Time Restriction
                  </button>
                )}
              <Modal
                isOpen={timeRestrictionOpen}
                className='update-applicant-status-modal'
                onRequestClose={() => setTimeRestrictionOpen(false)}
                ariaHideApp={false}
              >
                <div className='modal-head'>
                  <h4>Update Login Time Restriction</h4>
                </div>

                <div className='modal-body applicant-status-modal-body'>
                  <p>Set Login Expiry Date</p>
                  <div className='date-time-pickers time-restriction-picker'>
                    <DatePicker
                      selected={moment(startDate)}
                      onChange={(date) => setStartDate(date)}
                      showMonthDropdown
                      showYearDropdown
                      dateFormat={dateTimeConstants.DATE_FORMAT}
                      minDate={moment()}
                      dropdownMode='select'
                      customInput={<CustomDatePickerInput />}
                    />
                  </div>
                  <div className='button-holder button-holder--right'>
                    <button
                      className='button cancel'
                      onClick={() => setTimeRestrictionOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className='button'
                      onClick={handleSave}
                      disabled={mutation.isLoading}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
          </div>
        </>
      )}
      <section className='d-flex w-100'>
        <div className='col-vert pos-rel w-100'>
          <div className='doc-list-items'>
            <Tab.Container id='tabs-with-dropdown' activeKey={activeTab}>
              <Row>
                <Col sm={12}>
                  <div className={cx('m-0 custom-tabs', styles.p_custom)}>
                    <Nav bsStyle='tabs'>
                      {TABS.map((report) => (
                        <NavItem
                          eventKey={report.key}
                          className='custom-tab'
                          key={report.key}
                          onClick={() => setActiveTab(report.key)}
                        >
                          <div className={styles.tab_nav_item_txt}>
                            <img
                              src={
                                require(
                                  `assets/icons/${report.key}_${activeTab === report.key ? 'green' : 'grey'}.svg`,
                                ).default
                              }
                              alt=''
                            />
                            {report.title}
                          </div>
                        </NavItem>
                      ))}
                    </Nav>
                  </div>
                </Col>

                <Col sm={12}>
                  <div className='custom-tab-content'>
                    <Tab.Content animation>
                      <Tab.Pane eventKey='applicant'>
                        <ViewApplicant
                          hash={hash}
                          isApplicationEditable={isApplicationEditable}
                          applnType={applnType}
                          goBack={goBack}
                          email={email}
                          sendMail={sendMail}
                          editApplicant={editApplicant}
                          viewHistory={viewHistory}
                          enableStatusModal={enableStatusModal}
                          setEnableStatusModal={setEnableStatusModal}
                        />
                      </Tab.Pane>
                      <Tab.Pane eventKey='interview'>
                        <InterviewForm
                          hash={hash}
                          email={email}
                          applicant={applicants}
                        />
                      </Tab.Pane>
                      <Tab.Pane eventKey='no_reports_found'>
                        <NoAccess />
                      </Tab.Pane>
                    </Tab.Content>
                  </div>
                </Col>
              </Row>
            </Tab.Container>
          </div>
        </div>
      </section>
    </div>
  );
};

ApplicantsTab.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  hash: PropTypes.string,
  applnType: PropTypes.string,
  goBack: PropTypes.func,
  email: PropTypes.string,
  sendMail: PropTypes.func,
  editApplicant: PropTypes.func,
  viewHistory: PropTypes.func,
  applicantView: PropTypes.bool,
  isApplicationEditable: PropTypes.bool,
};

export default withRouter(ApplicantsTab);
